import {
    Box,
    Loader,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import IBAN from 'iban';
import type { IBankAccountDetailsLogGetAllResult } from '@common/interfaces';
import { ListPager } from '@frontend/components/elements/ListPager';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { getSelectedLanguage } from '@frontend/translate';

interface IBankAccountLogsModalProps {
    bankAccountLogEntries: IBankAccountDetailsLogGetAllResult;
    handleClose: () => void;
    isLoading: boolean;
    isOpen: boolean;
    pagination: {
        currentPage: number;
        itemsPerPage: number;
        onChange: (currentPage: number, itemsPerPage: number) => void;
    };
}

export function BankAccountLogsModal(props: Readonly<IBankAccountLogsModalProps>) {
    const translateKey = getSelectedLanguage();

    return (
        <Modal
            closeOnEsc
            closeOnOverlayClick
            isOpen={props.isOpen}
            onClose={props.handleClose}
            size="xlarge"
        >
            <Modal.Header>{translateKey.bankAccountDetailsLog.bankAccountDetailsChangesLog}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                {props.isLoading && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        mb={4}
                    >
                        <Loader size="medium" />
                    </Box>
                )}
                {!props.isLoading && (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>{translateKey.bankAccountDetailsLog.modificationDate}</TableHeader>
                                        <TableHeader>{translateKey.bankAccountDetailsLog.modifiedBy}</TableHeader>
                                        <TableHeader>{translateKey.bankAccountDetailsLog.details}</TableHeader>
                                        <TableHeader>{translateKey.bankAccountDetailsLog.action}</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {props.bankAccountLogEntries.items.map((log) => (
                                        <TableRow key={log.id}>
                                            <TableCell>
                                                <NullableFormattedDate value={log.createdAt} />
                                            </TableCell>
                                            <TableCell>{log.modifierPractitionerDisplayName}</TableCell>
                                            <TableCell>
                                                {IBAN.printFormat(log.modifiedBankAccountNumber)}
                                                <br />
                                                {log.modifiedBankAccountSubscriber}
                                            </TableCell>
                                            <TableCell>
                                                {translateKey.bankAccountDetailsActionEnum[log.action]}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <ListPager
                            itemsPerPage={props.pagination.itemsPerPage}
                            onChangeItemsPerPage={(itemsPerPage) => props.pagination.onChange(1, itemsPerPage)}
                            onChangePage={(page) => props.pagination.onChange(page, props.pagination.itemsPerPage)}
                            page={props.pagination.currentPage}
                            total={props.bankAccountLogEntries.total}
                        />
                    </>
                )}
            </Modal.Body>
            <Modal.Footer />
        </Modal>
    );
}
